import {useEffect} from 'react';

import * as sentry from '@sentry/react';
import {useTranslation} from 'react-i18next';

import {config} from 'config/env';
import {useAppDispatch, useAppSelector} from 'hooks';
import useBrowserEventListener from 'hooks/useBrowserEventListner';
import {useSelectedItemsLocalStorage} from 'hooks/useSelectedItemsLocalStorage';
import ReactNative from 'services/react-native';
import SendBrowserEvent from 'services/send-browser-event';
import {fetchUserAddressesAction} from 'store/actions/addresses-action';
import {
  setIsDirectCheckoutAction,
  fetchMakroMembershipCardAction,
  setAccessTradeClickIdAction,
  setPricezaClickIdAction,
  setAppModeAction,
  setAppVersionAction,
  setDeviceOSAction,
  setIsFromBasketNotification,
  setCurrentStoreWithLocationLocalesAction,
  setTrueIdAction,
  setOmniChannelsVoucherRedemptionEnabled,
  setMpidAction
} from 'store/actions/app-action';
import {getMetaDataAction} from 'store/actions/basket-action';
import {getTokenFromCookies, isSyncAddressEnabled, isUnifiedBasketEnabled} from 'utils/helper';
import BasketWrapper from 'views/basket-wrapper/BasketWrapper';

import {AppMode} from './constants';
import {setAuthTokensAction} from './store/actions/auth-action';

function App() {
  const dispatch = useAppDispatch();
  const {i18n} = useTranslation();

  const {idToken: idTokenFromRedux} = useAppSelector((state) => state?.auth) || {};

  const url = new URL(window.location.href);
  const {
    idToken: idTokenFromSearchParams,
    locale,
    subdistrict: subdistrictFromSearchParams,
    zipcode: zipcodeFromSearchParams,
    currentStoreCodes,
    expressStoreCodes: currentExpressStoreCodes,
    showStorePickup,
    isDirectCheckout,
    accessTradeClickId,
    pricezaClickId,
    trueId,
    mpid,
    appMode,
    appVersion,
    deviceOS,
    isSameDayDelivery,
    customerId,
    isFromBasketNotification,
    isOmniChannelVoucherEnabled
  } = Object.fromEntries(url.searchParams.entries());
  useBrowserEventListener(deviceOS);
  const garbageValues = ['undefined', 'null'];
  const storeCodes = currentStoreCodes ? currentStoreCodes.split(',') : [];
  const expressStoreCodes = currentExpressStoreCodes ? currentExpressStoreCodes.split(',') : [];
  const isSecureBasketCheckoutWithEventEnabled = config.IS_SECURE_BASKET_CHECKOUT_WITH_EVENT_ENABLED;
  const isSyncAddress = isSyncAddressEnabled();
  const selectedItemsLocalStorage = useSelectedItemsLocalStorage();

  const token =
    idTokenFromSearchParams && !garbageValues.includes(idTokenFromSearchParams)
      ? idTokenFromSearchParams
      : getTokenFromCookies('basketIdToken');

  const zipcode =
    zipcodeFromSearchParams && !garbageValues.includes(zipcodeFromSearchParams) ? zipcodeFromSearchParams : null;

  const subdistrict =
    subdistrictFromSearchParams && !garbageValues.includes(subdistrictFromSearchParams)
      ? subdistrictFromSearchParams
      : null;

  const conditionalAddressDep = isSyncAddress ? [] : [zipcode, subdistrict];

  useEffect(() => {
    sentry.setContext('query-params', {
      locale: locale,
      subdistrictFromSearchParams: subdistrictFromSearchParams,
      zipcodeFromSearchParams: zipcodeFromSearchParams,
      currentStoreCodes: currentStoreCodes,
      showStorePickup: showStorePickup,
      isDirectCheckout: isDirectCheckout,
      accessTradeClickId: accessTradeClickId,
      pricezaClickId: pricezaClickId,
      appMode: appMode,
      appVersion: appVersion,
      deviceOS: deviceOS,
      isSameDayDelivery: isSameDayDelivery,
      customerId: customerId,
      tok: idTokenFromSearchParams
    });
  }, [
    accessTradeClickId,
    appMode,
    appVersion,
    currentStoreCodes,
    customerId,
    deviceOS,
    idTokenFromSearchParams,
    isDirectCheckout,
    isSameDayDelivery,
    locale,
    pricezaClickId,
    showStorePickup,
    subdistrictFromSearchParams,
    zipcodeFromSearchParams
  ]);

  useEffect(() => {
    if (ReactNative.isReactNativeWebView()) {
      const bodyStyle = document.body.style;

      bodyStyle.userSelect = 'none'; // Modern browsers
      bodyStyle.webkitUserSelect = 'none'; // Safari and iOS
      bodyStyle.setProperty('msUserSelect', 'none'); // Internet Explorer 10/11
      bodyStyle.setProperty('MozUserSelect', 'none'); // Older Firefox
    }
  }, []);

  useEffect(() => {
    if (isSecureBasketCheckoutWithEventEnabled) {
      SendBrowserEvent.getIdToken();
    } else if (token) {
      dispatch(setAuthTokensAction({idToken: token, customerId: customerId}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (idTokenFromRedux && (isSyncAddress || (zipcode && subdistrict && storeCodes?.length))) {
      if (isSyncAddress) {
        dispatch(getMetaDataAction());
      } else {
        dispatch(
          setCurrentStoreWithLocationLocalesAction({
            zipcode: zipcode as string,
            subdistrict: subdistrict as string,
            storeCodes,
            showStorePickup: showStorePickup === 'true',
            isSameDayDelivery: isSameDayDelivery === 'true',
            expressStoreCodes
          })
        );
      }
      dispatch(fetchMakroMembershipCardAction(idTokenFromRedux));
      dispatch(fetchUserAddressesAction());
      dispatch(setIsDirectCheckoutAction(isDirectCheckout === 'true'));
      dispatch(setAccessTradeClickIdAction(accessTradeClickId));
      dispatch(setTrueIdAction(trueId));
      dispatch(setMpidAction(mpid));
      dispatch(setPricezaClickIdAction(pricezaClickId));
    }
    if (isUnifiedBasketEnabled()) {
      dispatch(setAppModeAction(AppMode.regular));
    } else if (appMode && !garbageValues.includes(appMode)) {
      dispatch(setAppModeAction(appMode as AppMode));
    }
    if (locale && !garbageValues.includes(locale)) {
      i18n.changeLanguage(locale);
    }
    if (appVersion && !garbageValues.includes(appVersion)) {
      dispatch(setAppVersionAction(appVersion));
    }
    if (deviceOS && !garbageValues.includes(deviceOS)) {
      dispatch(setDeviceOSAction(deviceOS));
    }
    if (isFromBasketNotification && !garbageValues.includes(isFromBasketNotification)) {
      dispatch(setIsFromBasketNotification(isFromBasketNotification === 'true'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, idTokenFromRedux, locale, accessTradeClickId, appMode, trueId, mpid, ...conditionalAddressDep]);

  useEffect(() => {
    dispatch(setOmniChannelsVoucherRedemptionEnabled(isOmniChannelVoucherEnabled === 'true'));
  }, [dispatch, isOmniChannelVoucherEnabled]);

  useEffect(() => {
    // we really passing 'undefined' string as idtoken
    if (idTokenFromSearchParams && idTokenFromSearchParams !== 'undefined') {
      return;
    }
    // if the app is in the logged out state, clear the saved state
    selectedItemsLocalStorage.remove();
  }, [idTokenFromSearchParams, selectedItemsLocalStorage]);

  return <BasketWrapper />;
}

export default App;
